import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';

import {
    SendEmailDocument,
    IEmailTypeEnum,
    IContentTypeValue,
} from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { ConfirmDialog } from 'common/components/ConfirmDialog';

interface IProps {
    trainingId: string;
    open: boolean;
    userIds: string[];
    onClose(): void;
    onComplete?: () => void;
}

export const TrainingInviteEmailDialog = ({
    userIds,
    trainingId,
    open,
    onClose,
    onComplete,
}: IProps) => {
    const [translate] = useTranslation();

    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();

    const [sendEmail, { loading: loadingSendEmail }] =
        useMutation(SendEmailDocument);

    const count = userIds.length;

    const handleSendEmail = async () => {
        let result;

        try {
            result = await sendEmail({
                variables: {
                    emailType: IEmailTypeEnum.TrainingInvite,
                    userIds,
                    relatedId: trainingId,
                    relatedType: IContentTypeValue.Training,
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        const { succeededIds = [], failedIds = [] } =
            result.data?.sendEmail?.response || {};

        if (succeededIds.length) {
            displaySnackbar(
                translate('trainingInviteEmail.trainingInviteEmailSent', {
                    count: succeededIds.length,
                }),
                {
                    variant: 'success',
                }
            );
        }

        if (failedIds.length) {
            displaySnackbar(
                translate('trainingInviteEmail.trainingInviteEmailFailed', {
                    count: failedIds.length,
                }),
                { variant: 'error' }
            );
        }

        onComplete?.();

        onClose();
    };

    return (
        <ConfirmDialog
            confirmText={translate('trainingInviteEmail.dialog.buttonText', {
                count,
            })}
            loading={loadingSendEmail}
            open={open}
            title={translate('trainingInviteEmail.dialog.title', {
                count,
            })}
            onCancel={onClose}
            onClose={onClose}
            onConfirm={handleSendEmail}
        >
            {translate('trainingInviteEmail.dialog.text', {
                count,
            })}
        </ConfirmDialog>
    );
};
